import { useEffect, useRef } from 'react'
import { ConvexClient } from 'convex/browser'
import { api } from '@aurora/convex/convex/_generated/api'
import { createPersistence, patchesToSnake } from '@aurora/platform'
import { AURORA_PLATFORM_CONFIG } from '../store/registry'
import { auroraStore } from '../store/auroraStore'

/**
 * The runtime binding between the local-first store and Convex.
 *
 * Renders nothing. Responsibilities, in strict order:
 *   1. hydrate from IndexedDB (critical -> deferred), then open write-through
 *   2. bind the dispatch function so the outbox can drain
 *   3. subscribe to delta queries and feed them through the sync engine
 *   4. refresh on focus / reconnect and drain the outbox
 *
 * Order matters: write-through must open only AFTER hydration, or a delta
 * arriving mid-hydration can prune the on-disk shadow.
 */

const TOKEN_KEY = 'aurora:token'

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setToken(token: string) {
  try {
    localStorage.setItem(TOKEN_KEY, token)
  } catch {
    /* private mode — the session simply will not persist */
  }
}

/** Child-scoped delta queries, keyed by their store collection. */
const CHILD_QUERIES = [
  // the open substrate
  ['threads', 'listThreads'],
  ['messages', 'listMessages'],
  ['captures', 'listCaptures'],
  ['artifacts', 'listArtifacts'],
  ['memories', 'listMemories'],
  ['schedules', 'listSchedules'],
  // the spine
  ['skills', 'listSkills'],
  ['skillStates', 'listSkillStates'],
  ['cycles', 'listCycles'],
  ['weeks', 'listWeeks'],
  ['days', 'listDays'],
  ['blocks', 'listBlocks'],
  ['levelHistory', 'listLevelHistory'],
  ['reviews', 'listReviews'],
] as const

export function ConvexSync({ convexUrl }: { convexUrl: string }) {
  const clientRef = useRef<ConvexClient | null>(null)
  const unsubsRef = useRef<Array<() => void>>([])
  const bootedRef = useRef(false)

  const token = getToken()
  const activeChildId = auroraStore((s: any) => s.ui.activeChildId)

  // ── boot: hydrate, then bind dispatch ──────────────────────
  useEffect(() => {
    if (bootedRef.current) return
    bootedRef.current = true

    const persistence = createPersistence(AURORA_PLATFORM_CONFIG, auroraStore as any)
    // Either way the wait is over: a failed read means an empty store, which
    // the views must show as empty rather than as a skeleton forever.
    void persistence
      .hydrate()
      .finally(() => (auroraStore.getState() as any).setHydrated())
  }, [])

  // ── dispatch binding ───────────────────────────────────────
  useEffect(() => {
    if (!token) return

    const client = new ConvexClient(convexUrl)
    clientRef.current = client

    const state = auroraStore.getState() as any
    // `owner` epochs the binding: an account switch or HMR invalidates
    // in-flight dispatches instead of letting them land on the wrong account.
    state._setDispatch(
      (actionName: string, args: unknown, patches: unknown, result: unknown, commandId?: string) =>
        client.mutation(api.dispatch.dispatch, {
          token,
          action: actionName,
          args,
          patches: patchesToSnake(patches),
          result,
          commandId,
        }),
      { owner: token },
    )

    return () => {
      clientRef.current = null
      client.close()
    }
  }, [convexUrl, token])

  // ── account, fetched once ──────────────────────────────────
  // Skill definitions are per-child rows on the spine, so there is no separate
  // framework fetch — they arrive through the same delta channel as everything.
  useEffect(() => {
    const client = clientRef.current
    if (!client || !token) return
    let cancelled = false

    void (async () => {
      const account = await client.query(api.sync.getAccount, { token })
      if (cancelled) return
      const s = auroraStore.getState() as any
      if (account) s.setAccount(account.account, account.clientState)
      if (account?.account?.active_child_id && !s.ui.activeChildId) {
        s.setActiveChild(account.account.active_child_id)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [token])

  // ── child list (account-scoped) ────────────────────────────
  useEffect(() => {
    const client = clientRef.current
    if (!client || !token) return
    return client.onUpdate(api.sync.listChildren, { token }, (page: any) => {
      ;(auroraStore.getState() as any).syncTable('children', page.rows)
    })
  }, [token])

  // ── child-scoped delta subscriptions ───────────────────────
  useEffect(() => {
    const client = clientRef.current
    if (!client || !token || !activeChildId) return

    unsubsRef.current.forEach((u) => u())
    unsubsRef.current = []

    for (const [storeKey, queryName] of CHILD_QUERIES) {
      const q = (api.sync as any)[queryName]
      if (!q) continue
      const unsub = client.onUpdate(
        q,
        { token, childId: activeChildId },
        (page: any) => {
          ;(auroraStore.getState() as any).syncTable(storeKey, page.rows)
          // A partial page means the server trimmed at a timestamp boundary;
          // ask again so the watermark keeps advancing without dropping rows.
          if (page.hasMore) {
            void client
              .query(q, { token, childId: activeChildId, since: page.nextSince })
              .then((next: any) => {
                if (next?.rows?.length) {
                  ;(auroraStore.getState() as any).syncTable(storeKey, next.rows)
                }
              })
          }
        },
      )
      unsubsRef.current.push(unsub)
    }

    return () => {
      unsubsRef.current.forEach((u) => u())
      unsubsRef.current = []
    }
  }, [token, activeChildId])

  // ── focus / connectivity: drain the outbox ─────────────────
  useEffect(() => {
    const s = () => auroraStore.getState() as any

    const drain = () => {
      s().setOnline(navigator.onLine)
      if (navigator.onLine) void s()._drainOutbox?.()
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') drain()
    }

    window.addEventListener('online', drain)
    window.addEventListener('offline', drain)
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', drain)
    drain()

    return () => {
      window.removeEventListener('online', drain)
      window.removeEventListener('offline', drain)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', drain)
    }
  }, [])

  return null
}
